import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

// 基礎容器樣式優化 - 添加右側留白
const Container = styled.div`
  position: absolute; 
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #f9f7f4;
  font-family: 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif;
  box-sizing: border-box;
  overflow: hidden;
  font-size: 16px;
  padding: 0 15px 0 15px; /* 左右兩側的留白 */
  
  @media (max-width: 480px) {
    font-size: 18px;
    padding: 0 20px 0 10px; /* 右側稍微多一點留白 */
  }
`;

// 頭部樣式優化 - 修改頭部寬度確保有留白
const Header = styled.header`
  display: flex;
  justify-content: center;
  padding: 15px;
  width: calc(100% - 8px); /* 略微縮小寬度以確保兩側有留白 */
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  margin: 10px 0;
  border-radius: 8px;
  
  @media (max-width: 480px) {
    width: 100%; /* 在移動端使用完整寬度，因為Container已經有padding */
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #3a3a3a;
  
  @media (max-width: 480px) {
    font-size: 18px;
  }
`;

const LogoImage = styled.img`
  height: 32px;
  margin-right: 10px;
  
  @media (max-width: 480px) {
    height: 28px;
    margin-right: 8px;
  }
`;

/// 進度條樣式優化 - 縮短線條長度，提高視覺效果
const ProgressBar = styled.div`
display: flex;
justify-content: space-between;
margin: 24px auto;
  width: 80%; /* 減少寬度 */
  max-width: 350px; /* 限制最大寬度 */
position: relative;

&::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 10%;
  right: 10%;
  height: 3px;
  background-color: #e0e0e0;
  z-index: 1;
}

@media (max-width: 480px) {
  width: 85%;  
  margin: 20px auto 30px auto;
  
  &::before {
    left: 15%;
    right: 15%;
  }
}
`;

const ProgressStep = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#a0c3bb' : '#e0e0e0'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 16px;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  
  &::after {
    content: '${props => props.title}';
    position: absolute;
    bottom: -22px;
    white-space: nowrap;
    font-size: 12px;
    color: ${props => props.active ? '#3a3a3a' : '#a0a0a0'};
    font-weight: ${props => props.active ? 'bold' : 'normal'};
  }
  
  @media (max-width: 480px) {
    width: 38px;
    height: 38px;
    font-size: 16px;
    
    &::after {
      font-size: 13px;
      bottom: -24px;
    }
  }
`;

// 內容區域樣式優化 - 確保右側有足夠留白
const Content = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 10px; /* 減少內部左右padding，讓Container的padding發揮作用 */
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  box-sizing: border-box;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-height: calc(100% - 20px);
  
  @media (max-width: 480px) {
    padding: 15px 5px 30px 5px; /* 減少內部左右padding */
  }
`;

const Title = styled.h1`
  color: #3a3a3a;
  font-size: 28px;
  font-weight: normal;
  text-align: center;
  margin: 15px 0 35px;
  width: 100%;
  word-wrap: break-word; /* 確保長詞可以換行 */
  
  @media (max-width: 480px) {
    font-size: 24px;
    margin: 10px 0 30px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 35px;
  width: 100%;
  justify-content: center;
  
  @media (max-width: 480px) {
    flex-direction: ${props => props.stack ? 'column' : 'row'};
    gap: 12px;
  }
`;

const Button = styled.button`
  background-color: #a0c3bb;
  color: white;
  border: none;
  padding: 12px 30px;
  font-size: 18px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #8ab0a8;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    padding: 12px 25px;
    font-size: 17px;
    width: ${props => props.fullWidth ? '100%' : 'auto'};
  }
`;

const BackButton = styled.button`
  background-color: white;
  color: #3a3a3a;
  border: 1px solid #e0e0e0;
  padding: 12px 30px;
  font-size: 18px;
  border-radius: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  @media (max-width: 480px) {
    padding: 12px 25px;
    font-size: 17px;
    width: ${props => props.fullWidth ? '100%' : 'auto'};
  }
`;

// 語言選擇頁優化
const LanguageGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 25px;
  width: 100%;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }
`;

// 更新 LanguageOption 組件 - 添加選擇狀態和樣式
const LanguageOption = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  margin: 10px;
  width: 120px;
  height: 120px;
  background-color: ${props => props.selected ? '#a0c3bb' : 'white'};
  border-radius: 10px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    background-color: ${props => props.selected ? '#8ab0a8' : '#f5f5f5'};
  }
  
  @media (max-width: 480px) {
    width: 100px;
    height: 100px;
    padding: 10px;
  }
`;

const Flag = styled.div`
  width: 70px;
  height: 52px;
  margin-bottom: 15px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  border-radius: 8px;
  
  @media (max-width: 480px) {
    width: 65px;
    height: 48px;
    font-size: 32px;
  }
`;

// 更新 LanguageName 組件 - 根據選擇狀態調整文字顏色
const LanguageName = styled.p`
  font-size: 18px;
  margin-top: 10px;
  color: ${props => props.selected ? 'white' : '#3a3a3a'};
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

// 視頻容器優化
const VideoContainer = styled.div`
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  position: relative;
  aspect-ratio: 16/9;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 480px) {
    border-radius: 10px;
  }
`;

const ResponsiveIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const VideoDescription = styled.p`
  margin: 20px 0;
  line-height: 1.6;
  text-align: center;
  color: #3a3a3a;
  padding: 0 10px;
  font-size: 17px;
  
  @media (max-width: 480px) {
    font-size: 17px;
    line-height: 1.5;
    margin: 25px 0;
  }
`;

// 激活碼頁面樣式優化
const ActivationForm = styled.div`
  background-color: white;
  padding: 30px 25px;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
  position: relative;
  
  p {
    margin-top: 0;
    margin-bottom: 25px;
    text-align: center;
    font-size: 17px;
  }
  
  @media (max-width: 480px) {
    padding: 25px 20px;
    margin: 0 auto;
    
    p {
      font-size: 17px;
      margin-bottom: 22px;
    }
  }
`;

const CodeInput = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 25px;
  
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`;

const InputDigit = styled.input`
  width: 50px;
  height: 60px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  text-align: center;
  font-size: 26px;
  font-weight: bold;
  
  &:focus {
    border-color: #a0c3bb;
    outline: none;
  }
  
  @media (max-width: 480px) {
    width: 42px;
    height: 55px;
    font-size: 24px;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 16px;
  margin: 12px 0;
  text-align: center;
  min-height: 20px;
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

// 修正 loading 動畫置中問題
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 12px;
  text-align: center;
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid #a0c3bb;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
  }
`;

const LoadingText = styled.div`
  margin-top: 15px;
  font-size: 18px;
  color: #3a3a3a;
`;

// 社交媒體按鈕樣式優化
const SocialButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 90%;
  max-width: 400px; /* 確保不會太寬 */
  margin: 15px auto;
  
  @media (max-width: 480px) {
    width: 90%;
  }
`;

const SocialButton = styled.a`
  display: flex;
  align-items: center;
  padding: 20px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
  
  &.disabled-link {
    opacity: 0.6;
    cursor: not-allowed;
    
    &:hover {
      transform: none;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    }
  }
  
  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const SocialIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #f5f5f5;
  margin-right: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 60%;
    height: 60%;
    object-fit: contain;
  }
  
  @media (max-width: 480px) {
    width: 45px;
    height: 45px;
    margin-right: 16px;
  }
`;

const SocialText = styled.p`
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  color: #3a3a3a;
  
  @media (max-width: 480px) {
    font-size: 17px;
  }
`;

// 條款同意樣式 - 更新以實現置中效果
const termsStyles = {
  termsContainer: {
    display: 'flex',
    alignItems: 'center', // 改為居中對齊
    justifyContent: 'center', // 添加水平居中
    marginTop: '20px',
    marginBottom: '25px',
    width: '100%',
    maxWidth: '400px',
    textAlign: 'center' // 添加文字置中
  },
  termsCheckbox: {
    marginRight: '10px',
    accentColor: '#a0c3bb'
  },
  termsLabel: {
    fontSize: '16px',
    lineHeight: '1.5',
    color: '#3a3a3a',
    display: 'flex', // 使標籤內容排列更靈活
    alignItems: 'center', // 垂直居中對齊
    justifyContent: 'center', // 水平居中對齊
    flexWrap: 'wrap' // 允許在必要時換行
  },
  termsLink: {
    color: '#a0c3bb',
    textDecoration: 'underline',
    margin: '0 5px' // 添加一些間距
  },
  termsLinkHover: {
    color: '#8ab0a8'
  }
};


// 更清晰的圖標定義
const icons = {
  // WhatsApp 圖標 - 綠色聊天泡泡
  whatsapp: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 448 512' width='100%25' height='100%25'%3E%3Cpath fill='%2325D366' d='M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c33.4 18.1 71.2 27.3 110.3 27.3 122.4 0 222-99.6 222-222 0-59.3-25.2-115.1-69.1-157.3zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z'/%3E%3C/svg%3E`,

  // Facebook Messenger 圖標 - 藍色
  facebook: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' width='100%25' height='100%25'%3E%3Cpath fill='%230084FF' d='M256.55 8C116.52 8 8 110.34 8 248.57c0 72.3 29.71 134.78 78.07 177.94 8.35 7.51 6.63 11.86 8.05 58.23a19.92 19.92 0 0 0 32 16.22c12.51-10.78 62.8-51.85 62.8-51.85 39.66 9.89 83.39 15.9 128.14 15.9 141.29 0 248.8-99.57 248.8-237.29C565.87 110.34 457.36 8 256.55 8zm-71.01 339.19c-25.9 0-47.06-20.96-47.06-46.7 0-25.78 21.14-46.76 47.06-46.76 25.9 0 47.06 20.98 47.06 46.76 0 25.74-21.16 46.7-47.06 46.7zm118.29 0c-25.9 0-47.06-20.96-47.06-46.7 0-25.78 21.14-46.76 47.06-46.76 25.9 0 47.06 20.98 47.06 46.76 0 25.74-21.16 46.7-47.06 46.7zm118.87 0c-25.9 0-47.06-20.96-47.06-46.7 0-25.78 21.14-46.76 47.06-46.76 25.9 0 47.06 20.98 47.06 46.76 0 25.74-21.16 46.7-47.06 46.7z'/%3E%3C/svg%3E`,

  // 網絡應用圖標 - 藍色地球
  webapp: `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' width='100%25' height='100%25'%3E%3Cpath fill='%234285F4' d='M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256 256-114.6 256-256S397.4 0 256 0zm0 464c-114.7 0-208-93.31-208-208S141.3 48 256 48s208 93.31 208 208-93.3 208-208 208zm-32-112h64c8.8 0 16-7.2 16-16v-96h66.7c10.1 0 15.3-12.2 8.2-19.4L260.6 102.3c-4.7-4.7-12.3-4.7-17 0L125.2 220.6c-7.1 7.2-2 19.4 8.2 19.4H200v96c0 8.8 7.16 16 16 16z'/%3E%3C/svg%3E`
};

// 使用style標籤直接插入全局樣式
const GlobalStyles = () => {
  useEffect(() => {
    // 創建樣式元素
    const styleElement = document.createElement('style');
    styleElement.type = 'text/css';
    styleElement.innerHTML = `
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          width: 100% !important;
          height: 100% !important;
          overflow: hidden !important;
          position: fixed !important;
          touch-action: manipulation !important;
          -webkit-tap-highlight-color: transparent !important;
          -webkit-touch-callout: none !important;
          -webkit-user-select: none !important;
          user-select: none !important;
        }
        
        * {
          box-sizing: border-box !important;
          max-width: 100% !important;
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }
        
        *::-webkit-scrollbar {
          display: none !important;
        }
      `;

    // 添加到文檔頭部
    document.head.appendChild(styleElement);

    // 清理函數
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return null;
};

// 防止觸摸滾動的組件
const PreventScroll = () => {
  useEffect(() => {
    const preventDefault = (e) => {
      e.preventDefault();
    };

    // 阻止默認的觸摸移動行為
    document.body.addEventListener('touchmove', preventDefault, { passive: false });

    // 清理
    return () => {
      document.body.removeEventListener('touchmove', preventDefault);
    };
  }, []);

  return null;
};

const App = () => {
  const [step, setStep] = useState(1);
  const [language, setLanguage] = useState('en');
  const [activationCode, setActivationCode] = useState(['', '', '', '', '', '']);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [isChecked, setIsChecked] = useState(false); // 添加這個狀態用於勾選框

  // 為激活碼每個輸入框創建ref
  const inputRefs = useRef([]);

  // 初始化ref
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, 6);
  }, []);

  const nextStep = () => {
    if (step < 4) {
      setStep(step + 1);
      setVerificationError('');
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
      setVerificationError('');
    }
  };

  const selectLanguage = (lang) => {
    setLanguage(lang);
    // 不再自動調用 nextStep()，而是讓用戶勾選條款後點擊 Continue 按鈕
  };


  // 處理激活碼輸入 - 修改這個函數以實現自動跳轉
  const handleCodeChange = (index, e) => {
    const value = e.target.value;

    // 只允許數字，限制長度為1
    if (!/^\d*$/.test(value) || value.length > 1) return;

    const newCode = [...activationCode];

    // 如果用戶輸入了一個字符
    if (value.length === 1) {
      newCode[index] = value;
      setActivationCode(newCode);

      // 自動跳到下一個輸入框
      if (index < 5) {
        setTimeout(() => {
          inputRefs.current[index + 1].focus();
        }, 0);
      }

      // 如果是最後一格且所有格都填了，自動驗證
      if (index === 5 && !newCode.includes('')) {
        verifyCode(newCode);
      }
    } else {
      // 處理刪除操作
      newCode[index] = '';
      setActivationCode(newCode);
    }
  };

  // 處理按鍵行為
  const handleKeyDown = (index, e) => {
    // 如果按下退格鍵且當前框為空，跳回上一個框
    if (e.key === 'Backspace' && activationCode[index] === '' && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  // 當用戶粘貼內容時處理
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    // 如果粘貼的內容是6位數字
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setActivationCode(newCode);

      // 自動跳到最後一個輸入框
      setTimeout(() => {
        inputRefs.current[5].focus();
      }, 0);

      // 自動驗證
      setTimeout(() => {
        verifyCode(newCode);
      }, 300);
    }
  };

  // 驗證激活碼
  const verifyCode = async (code = activationCode) => {
    const codeString = code.join('');

    // 如果有空格，不驗證
    if (code.includes('')) return;

    console.log(codeString);

    setIsVerifying(true);
    setVerificationError('');

    try {
      // 實際 API 調用
      const response = await fetch('https://nkzee8u8cc.execute-api.ap-southeast-1.amazonaws.com/validateAccessCode', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: codeString }),
      });

      const data = await response.json();

      // 解析 API 響應
      if (response.ok && data.valid) {
        // 如果 API 返回的 customerId 存在，設置它
        if (data.customerId) {
          setSerialNumber(data.customerId);
        }

        setIsVerifying(false);
        nextStep();
      } else {
        setIsVerifying(false);
        setVerificationError(language === 'en'
          ? data.message || 'Invalid activation code. Please try again.'
          : '활성화 코드가 잘못되었습니다. 다시 시도하세요.');
      }
    } catch (error) {
      console.error('Error validating code:', error);
      setIsVerifying(false);
      setVerificationError(language === 'en'
        ? 'Network error. Please try again.'
        : '네트워크 오류. 다시 시도하세요.');
    }
  };

  // 多語言文本
  const texts = {
    en: {
      steps: ['Language', 'Introduction', 'Activation', 'Connect'],
      selectLanguage: "Please Select Your Language",
      agree: "I agree to the",
      termsAndConditions: "terms and conditions",
      continue: "Continue",
      titles: ['Please Select Your Language', 'Wellness SPA Introduction', 'Enter Your Activation Code', 'Connect with Us'],
      videoDesc: 'Experience our world-class SPA treatments and facilities, designed for your ultimate relaxation and rejuvenation.',
      activationDesc: 'Please enter the 6-digit code provided by our receptionist:',
      socialButtons: [
        { name: 'WhatsApp AI Assistant', icon: 'whatsapp', disabled: false },
        { name: 'Facebook AI Assistant', icon: 'facebook', disabled: false },
        { name: 'Webapp AI Assistant', icon: 'webapp', disabled: false }
      ],
      buttons: { back: 'Back', next: 'Continue', verify: 'Verify' },
      verifying: 'Verifying...'
    },
    ko: {
      steps: ['언어', '소개', '활성화', '연결'],
      selectLanguage: "언어를 선택해 주세요",
      agree: "본인은",
      termsAndConditions: "이용약관에 동의합니다",
      continue: "계속하기",
      titles: ['언어를 선택하세요', '웰니스 스파 소개', '활성화 코드 입력', '연결하기'],
      videoDesc: '최고의 휴식과 재생을 위해 설계된 세계적 수준의 스파 트리트먼트와 시설을 경험하세요.',
      activationDesc: '리셉션에서 제공한 6자리 코드를 입력하세요:',
      socialButtons: [
        { name: '왓츠앱 AI 어시스턴트', icon: 'whatsapp', disabled: false },
        { name: '페이스북 AI 어시스턴트', icon: 'facebook', disabled: true },
        { name: '웹 AI 어시스턴트', icon: 'webapp', disabled: false }
      ],
      buttons: { back: '뒤로', next: '계속', verify: '확인' },
      verifying: '확인 중...'
    }
  };

  // 비디오 URL
  const videoUrls = {
    en: 'https://www.youtube.com/embed/REua_KePuj0?si=5tKDbIznJXgFXtV5',
    ko: 'https://www.youtube.com/embed/mPNatQrCB0k?si=17GKgZk_L-TWl2-y'
  };

  const currentText = texts[language];

  // 生成社交媒體按鈕的URL
  const getSocialUrl = (icon, disabled) => {
    if (disabled) return '#';

    const code = serialNumber || 'NA';

    switch (icon) {
      case 'whatsapp':
        return `https://wa.me/85293789122?text=/start%20qr_code--spa`;
      case 'facebook':
        return `https://app.usesalesbot.io/p/facebook/vzjubqaifiuo5s9q?ref=spa_checkin`;
      case 'webapp':
        return `https://app.usesalesbot.io/p/web/vzjubqaifiuo5s9q/full?ref=spa_checkin`;
      default:
        return '#';
    }
  };

  const renderContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Title>{texts[language].selectLanguage}</Title>
            <LanguageGrid>
              <LanguageOption
                onClick={() => selectLanguage('en')}
                selected={language === 'en'}
              >
                <Flag>🇬🇧</Flag>
                <LanguageName selected={language === 'en'}>English</LanguageName>
              </LanguageOption>
              <LanguageOption
                onClick={() => selectLanguage('ko')}
                selected={language === 'ko'}
              >
                <Flag>🇰🇷</Flag>
                <LanguageName selected={language === 'ko'}>한국어</LanguageName>
              </LanguageOption>
            </LanguageGrid>

            {/* 條款同意選項 - 使用翻譯 */}
            <div style={termsStyles.termsContainer}>
              <input
                type="checkbox"
                id="termsCheckbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                style={termsStyles.termsCheckbox}
              />
              <label htmlFor="termsCheckbox" style={termsStyles.termsLabel}>
                {texts[language].agree}{" "}
                <a
                  href="https://www.katlai.com/app-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={termsStyles.termsLink}
                  onMouseOver={(e) => (e.target.style.color = termsStyles.termsLinkHover.color)}
                  onMouseOut={(e) => (e.target.style.color = termsStyles.termsLink.color)}
                >
                  {texts[language].termsAndConditions}
                </a>
              </label>
            </div>

            <ButtonContainer>
              <Button onClick={() => nextStep()} disabled={!isChecked || !language}>
                {texts[language].continue}
              </Button>
            </ButtonContainer>
          </>
        );
      case 2:
        return (
          <>
            <Title>{currentText.titles[1]}</Title>
            <VideoContainer>
              <ResponsiveIframe
                src={videoUrls[language]}
                title="Wellness SPA Introduction"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </VideoContainer>
            <VideoDescription>{currentText.videoDesc}</VideoDescription>
            <ButtonContainer>
              <BackButton onClick={prevStep}>{currentText.buttons.back}</BackButton>
              <Button onClick={nextStep}>{currentText.buttons.next}</Button>
            </ButtonContainer>
          </>
        );
      case 3:
        return (
          <>
            <Title>{currentText.titles[2]}</Title>
            <ActivationForm>
              <p>{currentText.activationDesc}</p>
              <CodeInput>
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <InputDigit
                    key={index}
                    maxLength="1"
                    value={activationCode[index]}
                    onChange={(e) => handleCodeChange(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    ref={(el) => (inputRefs.current[index] = el)}
                    autoFocus={index === 0 && step === 3}
                  />
                ))}
              </CodeInput>
              <ErrorMessage>{verificationError}</ErrorMessage>
              <ButtonContainer stack fullWidth>
                <BackButton onClick={prevStep} disabled={isVerifying} fullWidth>
                  {currentText.buttons.back}
                </BackButton>
                <Button
                  onClick={() => verifyCode()}
                  disabled={activationCode.includes('') || isVerifying}
                  fullWidth
                >
                  {currentText.buttons.verify}
                </Button>
              </ButtonContainer>
              {isVerifying && (
                <LoadingOverlay>
                  <div>
                    <Spinner />
                    <div style={{ marginTop: '10px' }}>{currentText.verifying}</div>
                  </div>
                </LoadingOverlay>
              )}
            </ActivationForm>
          </>
        );
      case 4:
        return (
          <>
            <Title>{currentText.titles[3]}</Title>
            <SocialButtonsContainer>
              {currentText.socialButtons.map((button, index) => (
                <SocialButton
                  key={index}
                  href={getSocialUrl(button.icon, button.disabled)}
                  className={button.disabled ? 'disabled-link' : ''}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SocialIcon>
                    <img src={icons[button.icon]} alt={button.name} />
                  </SocialIcon>
                  <SocialText>{button.name}</SocialText>
                </SocialButton>
              ))}
            </SocialButtonsContainer>
            <ButtonContainer>
              <BackButton onClick={prevStep}>{currentText.buttons.back}</BackButton>
            </ButtonContainer>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <GlobalStyles />
      <PreventScroll />
      <Container>
        <Header>
          <Logo>
            <LogoImage src="/favicon.ico" alt="Company Logo" />
          </Logo>
        </Header>

        <ProgressBar>
          {currentText.steps.map((stepTitle, index) => (
            <ProgressStep
              key={index}
              active={step >= index + 1}
              title={stepTitle}
            >
              {index + 1}
            </ProgressStep>
          ))}
        </ProgressBar>

        <Content>
          {renderContent()}
        </Content>
      </Container>
    </>
  );
};

export default App;